<template>
  <AppDialog
    :title="isReassign ? 'Reassign access token' : 'Create access token'"
    size="smedium"
    :show="show"
    append-to-body
    data-testid="api-token-form-dialog"
    @close="$emit('close')"
  >
    <form
      autocomplete="off"
      data-testid="api-token-form"
      @submit.prevent="submit"
    >
      <div
        v-if="serverError"
        class="alert alert-danger"
        data-testid="api-token-form-error"
      >{{ serverError }}</div>

      <div
        v-if="!isReassign"
        class="form-group"
      >
        <label
          class="control-label"
          for="api-token-name"
        >Name <abbr title="required">*</abbr></label>
        <AppInput
          id="api-token-name"
          v-model="form.name"
          type="text"
          placeholder="e.g. ci-pipeline, data-sync"
          required
        />
      </div>

      <div class="form-group">
        <label
          class="control-label"
          for="api-token-assignee"
        >Assigned member <abbr title="required">*</abbr></label>
        <AppLazySelect
          ref="assigneeSelect"
          v-model="selectedAssignee"
          data-testid="api-token-assignee"
          resource="member"
          eager
          :fetcher="fetchAssignableUsers"
        >
          <template #single-result-leading="{ value: result }">
            <AppAvatar
              v-if="result.avatar"
              :avatar="result.avatar"
              size="speck"
            />
          </template>
          <template #item-leading="{ item }">
            <AppAvatar
              v-if="item.avatar"
              :avatar="item.avatar"
              size="speck"
            />
          </template>
          <template #placeholder>Select a member…</template>
        </AppLazySelect>
        <small class="text-muted">The token acts as this member, with their permissions.</small>
      </div>

      <div
        v-if="!isReassign"
        class="form-group"
      >
        <label
          class="control-label"
          for="api-token-expiry"
        >Expiration</label>
        <select
          id="api-token-expiry"
          v-model="form.expires_in_days"
          class="form-control"
          data-testid="api-token-expiry"
        >
          <option value="never">Never (no expiry)</option>
          <option
            v-for="days in expiryOptions"
            :key="days"
            :value="days"
          >{{ days }} days</option>
        </select>
      </div>
    </form>

    <template #footer>
      <AppButton
        type="default"
        data-testid="api-token-cancel"
        @click="$emit('close')"
      >Cancel</AppButton>
      <AppButton
        type="primary"
        :disabled="!canSubmit"
        data-testid="api-token-submit"
        @click="submit"
      >
        {{ isReassign ? 'Reassign token' : 'Create token' }}
      </AppButton>
    </template>
  </AppDialog>
</template>

<script>
import api from '~/api';

export default {
  name: 'ApiTokenFormDialog',
  props: {
    show: { type: Boolean, default: false },
    organizationId: { type: [Number, String], required: true },
    expiryOptions: { type: Array, default: () => [] },
    // When set, the dialog reassigns this token instead of creating a new one.
    token: { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      submitting: false,
      // Gates the shared Inertia error bag so a stale error from an earlier submit isn't shown.
      submitted: false,
      form: this.blankForm(),
      selectedAssignee: this.blankAssignee(),
    };
  },
  computed: {
    isReassign() {
      return Boolean(this.token);
    },
    canSubmit() {
      if (this.submitting || !this.form.user_id) {return false;}

      return this.isReassign || this.form.name.trim().length > 0;
    },
    // Surface server-side validation / rate-limit errors so a rejected submit keeps the form open.
    serverError() {
      if (!this.submitted) {return null;}
      const errors = this.$page && this.$page.props && this.$page.props.errors;

      if (!errors) {return null;}
      // Permission-denied responses set `errors` to a bare string; validation errors use a field→messages map.
      if (typeof errors === 'string') {return errors;}
      const messages = Object.values(errors).flat().filter(Boolean);

      return messages.length ? messages.join(' ') : null;
    },
  },
  watch: {
    show(opened) {
      if (!opened) {return;}

      this.form = this.blankForm();
      this.selectedAssignee = this.blankAssignee();
      this.submitting = false;
      this.submitted = false;
    },
    selectedAssignee(member) {
      this.form.user_id = member ? member.id : null;
    },
  },
  methods: {
    blankForm() {
      if (this.token) {
        return { name: this.token.name, user_id: this.token.user_id };
      }

      return { name: '', user_id: null, expires_in_days: 'never' };
    },
    // Seeds the picker with the current assignee for the reassign case (ajax options aren't preloaded).
    blankAssignee() {
      return this.token ? { id: this.token.user_id, name: this.token.user_name } : null;
    },
    // Server-side searched + paginated so organizations with many members stay fast.
    fetchAssignableUsers(payload) {
      return api.request(
        `/organizations/${this.organizationId}/settings/api_tokens/assignable_users`,
        'GET',
        payload,
      );
    },
    submit() {
      if (!this.canSubmit) {return;}

      this.submitting = true;
      this.submitted = true;
      const options = {
        preserveScroll: true,
        onSuccess: () => this.$emit('close'),
        onFinish: () => { this.submitting = false; },
      };

      if (this.isReassign) {
        this.$inertia.patch(
          `/organizations/${this.organizationId}/settings/api_tokens/${this.token.id}`,
          { organization_access_token: { user_id: this.form.user_id } },
          options,
        );
      } else {
        this.$inertia.post(
          `/organizations/${this.organizationId}/settings/api_tokens`,
          { organization_access_token: this.form },
          options,
        );
      }
    },
  },
};
</script>
